/**
 * Centralized in-app replacements for window.confirm() and window.prompt().
 *
 * Usage from any component:
 *
 *   import { openConfirm, openPrompt } from "@/lib/dialogs";
 *
 *   if (!await openConfirm({
 *     title: "Delete partner?",
 *     message: "This cannot be undone.",
 *     confirmLabel: "Delete",
 *     danger: true,
 *   })) return;
 *
 *   const newAmt = await openPrompt({
 *     title: "Override standing amount",
 *     message: "Enter new amount in INR.",
 *     defaultValue: "5000",
 *     validate: (v) => (!isNaN(Number(v)) && Number(v) >= 0 ? null : "Enter a non-negative number"),
 *   });
 *   if (newAmt == null) return; // user cancelled
 *
 * The host component <DialogHost /> is mounted once at the App root and listens
 * to a tiny pub/sub. Callers get a Promise that resolves with the user's
 * choice (boolean for confirm, string|null for prompt — null = cancelled).
 */
import React, { useEffect, useState } from "react";
import { Modal, Btn, Input } from "@/components/Shared";

// --- Tiny pub/sub ----------------------------------------------------------
let _next = 1;
const _subs = new Set();
const _publish = (req) => _subs.forEach((fn) => fn(req));

export function openConfirm(opts = {}) {
  return new Promise((resolve) => {
    _publish({
      kind: "confirm",
      id: _next++,
      title: opts.title || "Are you sure?",
      message: opts.message || "",
      confirmLabel: opts.confirmLabel || "Confirm",
      cancelLabel: opts.cancelLabel || "Cancel",
      danger: !!opts.danger,
      resolve,
    });
  });
}

export function openPrompt(opts = {}) {
  return new Promise((resolve) => {
    _publish({
      kind: "prompt",
      id: _next++,
      title: opts.title || "Enter a value",
      message: opts.message || "",
      defaultValue: opts.defaultValue ?? "",
      placeholder: opts.placeholder || "",
      confirmLabel: opts.confirmLabel || "Save",
      cancelLabel: opts.cancelLabel || "Cancel",
      type: opts.type || "text", // 'text' | 'number' | 'textarea'
      validate: opts.validate, // (val) => null | "error string"
      resolve,
    });
  });
}

// --- Single mount point ----------------------------------------------------
export function DialogHost() {
  const [req, setReq] = useState(null);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    const handler = (r) => {
      setReq(r);
      setDraft(r.kind === "prompt" ? String(r.defaultValue ?? "") : "");
      setErr("");
    };
    _subs.add(handler);
    return () => _subs.delete(handler);
  }, []);

  const close = (val) => {
    if (req) req.resolve(val);
    setReq(null);
    setErr("");
  };
  const cancel = () => close(req?.kind === "prompt" ? null : false);
  const confirm = () => {
    if (!req) return;
    if (req.kind === "confirm") return close(true);
    if (req.validate) {
      const e = req.validate(draft);
      if (e) { setErr(e); return; }
    }
    close(draft);
  };

  if (!req) return null;
  const isPrompt = req.kind === "prompt";

  return (
    <Modal open onClose={cancel} title={req.title} testid={`dialog-${req.kind}`}>
      <div className="space-y-4">
        {req.message && (
          <p className="text-[13px] text-[var(--ink-2)] leading-relaxed whitespace-pre-line">{req.message}</p>
        )}
        {isPrompt && (
          <div>
            {req.type === "textarea" ? (
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setErr(""); }}
                placeholder={req.placeholder}
                rows={3}
                data-testid="dialog-prompt-input"
                className="w-full px-3 py-2 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] resize-y"
              />
            ) : (
              <Input
                autoFocus
                type={req.type === "number" ? "number" : "text"}
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setErr(""); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && req.type !== "textarea") { e.preventDefault(); confirm(); }
                }}
                placeholder={req.placeholder}
                data-testid="dialog-prompt-input"
              />
            )}
            {err && <div className="text-[12px] text-[var(--negative)] mt-1.5">{err}</div>}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={cancel} testid="dialog-cancel">{req.cancelLabel}</Btn>
          <Btn
            variant={req.danger ? "danger" : "primary"}
            onClick={confirm}
            testid="dialog-confirm"
          >
            {req.confirmLabel}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
